import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Plus, Edit, Trash2, Loader2, ArrowLeft, Save, X, FileText, Globe, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { adminBuildLogsAPI } from '../../services/adminApi';
import ImageUploader from '../../components/ImageUploader';
import { useLanguage } from '../../contexts/LanguageContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api')) return `${API_BASE_URL}${url}`;
  return `${API_BASE_URL}/api/uploads/image/${url}`;
};

// List Component
export const AdminBuildLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(null);
  const { t } = useLanguage();

  const fetchLogs = async () => {
    try {
      const data = await adminBuildLogsAPI.getAll();
      setLogs(data);
    } catch (err) {
      toast.error(t('admin.toast.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.common.confirmDelete'))) return;
    
    try {
      await adminBuildLogsAPI.delete(id);
      toast.success(t('admin.toast.deleted'));
      fetchLogs();
    } catch (err) {
      toast.error(t('admin.toast.error'));
    }
  };

  const handleReorder = async (id, direction) => {
    setReordering(id);
    try {
      await adminBuildLogsAPI.reorder(id, direction);
      await fetchLogs();
    } catch (err) {
      toast.error(t('admin.toast.error'));
    } finally {
      setReordering(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#00F0FF' }} />
      </div>
    );
  }

  return (
    <div data-testid="admin-build-logs">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold" style={{ color: '#F8FAFC' }}>{t('admin.buildLogs.title')}</h1>
        <Link
          to="/admin/build-logs/new"
          className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
          style={{
            background: 'linear-gradient(90deg, #00F0FF 0%, #0F172A 100%)',
            color: '#F8FAFC',
            border: '2px solid #00F0FF',
          }}
        >
          <Plus size={18} />
          {t('admin.buildLogs.newLog')}
        </Link>
      </div>

      <div className="glass-card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(0, 240, 255, 0.2)' }}>
              <th className="text-left p-4 w-16" style={{ color: '#94A3B8' }}>{t('admin.common.order') || 'Sıra'}</th>
              <th className="text-left p-4" style={{ color: '#94A3B8' }}>{t('admin.buildLogs.table.image')}</th>
              <th className="text-left p-4" style={{ color: '#94A3B8' }}>{t('admin.buildLogs.table.title')}</th>
              <th className="text-left p-4" style={{ color: '#94A3B8' }}>{t('admin.buildLogs.table.date')}</th>
              <th className="text-left p-4" style={{ color: '#94A3B8' }}>{t('admin.buildLogs.table.languages')}</th>
              <th className="text-right p-4" style={{ color: '#94A3B8' }}>{t('admin.buildLogs.table.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log, index) => (
              <tr key={log.id} style={{ borderBottom: '1px solid rgba(0, 240, 255, 0.1)' }}>
                <td className="p-4">
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => handleReorder(log.id, 'up')}
                      disabled={index === 0 || reordering === log.id}
                      className="p-1 rounded transition-colors disabled:opacity-30"
                      style={{ color: '#00F0FF', background: 'rgba(0, 240, 255, 0.1)' }}
                      data-testid={`reorder-up-${log.id}`}
                    >
                      <ChevronUp size={16} />
                    </button>
                    <button
                      onClick={() => handleReorder(log.id, 'down')}
                      disabled={index === logs.length - 1 || reordering === log.id}
                      className="p-1 rounded transition-colors disabled:opacity-30"
                      style={{ color: '#00F0FF', background: 'rgba(0, 240, 255, 0.1)' }}
                      data-testid={`reorder-down-${log.id}`}
                    >
                      <ChevronDown size={16} />
                    </button>
                  </div>
                </td>
                <td className="p-4">
                  <div className="w-16 h-12 rounded overflow-hidden" style={{ background: 'rgba(0, 240, 255, 0.1)' }}>
                    {log.image ? (
                      <img 
                        src={getImageUrl(log.image)} 
                        alt={log.title} 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FileText size={20} style={{ color: '#64748B' }} />
                      </div>
                    )}
                  </div>
                </td>
                <td className="p-4">
                  <h3 className="font-medium" style={{ color: '#F8FAFC' }}>{log.title}</h3>
                  <p className="text-sm mt-1 line-clamp-1" style={{ color: '#94A3B8' }}>{log.excerpt}</p>
                </td>
                <td className="p-4" style={{ color: '#94A3B8' }}>
                  {new Date(log.date).toLocaleDateString('tr-TR')}
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-1">
                    <span className="px-2 py-1 text-xs rounded" style={{ background: 'rgba(0, 240, 255, 0.2)', color: '#00F0FF' }}>EN</span>
                    {log.title_tr && (
                      <span className="px-2 py-1 text-xs rounded" style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#EF4444' }}>TR</span>
                    )}
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-2">
                    <Link
                      to={`/admin/build-logs/${log.id}`}
                      className="p-2 rounded-lg"
                      style={{ color: '#00F0FF', background: 'rgba(0, 240, 255, 0.1)' }}
                    >
                      <Edit size={16} />
                    </Link>
                    <button
                      onClick={() => handleDelete(log.id)}
                      className="p-2 rounded-lg"
                      style={{ color: '#EF4444', background: 'rgba(239, 68, 68, 0.1)' }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {logs.length === 0 && (
          <div className="p-8 text-center" style={{ color: '#94A3B8' }}>
            {t('admin.buildLogs.noLogs')}
          </div>
        )}
      </div>
    </div>
  );
};

// Form Component with Language Tabs
export const AdminBuildLogForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { t } = useLanguage();
  
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('en'); // 'en' or 'tr'
  const [formData, setFormData] = useState({
    // English (default)
    title: '',
    excerpt: '',
    content: '',
    // Turkish
    title_tr: '',
    excerpt_tr: '',
    content_tr: '',
    // Common
    image: '',
    images: [],
    video_url: '',
    video_file: '',
    video_orientation: 'landscape',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (isEdit) {
      const fetchLog = async () => {
        try {
          const data = await adminBuildLogsAPI.getById(id);
          setFormData({
            title: data.title || '',
            excerpt: data.excerpt || '',
            content: data.content || '',
            title_tr: data.title_tr || '',
            excerpt_tr: data.excerpt_tr || '',
            content_tr: data.content_tr || '',
            image: data.image || '',
            images: data.images || [],
            video_url: data.video_url || '',
            video_file: data.video_file || '',
            video_orientation: data.video_orientation || 'landscape',
            date: data.date || ''
          });
        } catch (err) {
          toast.error(t('admin.toast.error'));
          navigate('/admin/build-logs');
        } finally {
          setLoading(false);
        }
      };
      fetchLog();
    }
  }, [id, isEdit, navigate, t]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (url) => {
    setFormData({ ...formData, image: url });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEdit) {
        await adminBuildLogsAPI.update(id, formData);
        toast.success(t('admin.toast.updated'));
      } else {
        await adminBuildLogsAPI.create(formData);
        toast.success(t('admin.toast.saved'));
      }
      navigate('/admin/build-logs');
    } catch (err) {
      toast.error(t('admin.toast.error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#00F0FF' }} />
      </div>
    );
  }

  return (
    <div data-testid="admin-build-log-form">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/admin/build-logs')}
          className="p-2 rounded-lg"
          style={{ color: '#94A3B8', background: 'rgba(148, 163, 184, 0.1)' }}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold" style={{ color: '#F8FAFC' }}>
          {isEdit ? t('admin.buildLogs.editLog') : t('admin.buildLogs.newLog')}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="glass-card p-6 space-y-6">
        {/* Image and Date - Common for all languages */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div>
            <ImageUploader
              value={formData.image}
              onChange={handleImageChange}
              label={t('admin.buildLogs.form.coverImage')}
              placeholder={t('admin.buildLogs.form.uploadImage')}
              aspectRatio="16/9"
            />
          </div>
          <div className="lg:col-span-4 space-y-4 pt-2 border-t" style={{ borderColor: 'rgba(0,240,255,0.15)' }}>
            <label className="block text-sm" style={{ color: '#F8FAFC' }}>Ek Fotoğraflar / Additional Photos</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {(formData.images || []).map((img, i) => (
                <div key={i}>
                  <ImageUploader value={img} onChange={(url) => {
                    const arr = [...formData.images]; arr[i] = url; setFormData({ ...formData, images: arr });
                  }} label={`#${i + 1}`} aspectRatio="1/1" />
                  <button type="button" data-testid={`buildlog-remove-image-${i}`} onClick={() => {
                    setFormData({ ...formData, images: formData.images.filter((_, x) => x !== i) });
                  }} className="mt-1 text-xs" style={{ color: '#EF4444' }}>Kaldır</button>
                </div>
              ))}
              <button type="button" data-testid="buildlog-add-image" onClick={() => setFormData({ ...formData, images: [...(formData.images || []), ''] })}
                className="min-h-[100px] rounded-lg text-sm" style={{ border: '1px dashed rgba(0,240,255,0.4)', color: '#00F0FF' }}>+ Fotoğraf Ekle</button>
            </div>

            <label className="block text-sm pt-2" style={{ color: '#F8FAFC' }}>Video (isteğe bağlı)</label>
            <div className="flex gap-3 items-center flex-wrap">
              <select name="video_orientation" value={formData.video_orientation} onChange={handleChange}
                data-testid="buildlog-video-orientation" className="px-3 py-2 rounded-lg" style={{ background: 'rgba(15,23,42,0.6)', color: '#F8FAFC', border: '1px solid rgba(0,240,255,0.3)' }}>
                <option value="landscape">Yatay (16:9)</option>
                <option value="portrait">Dikey (9:16)</option>
              </select>
              <input type="file" accept="video/*" data-testid="buildlog-video-file" onChange={async (e) => {
                const f = e.target.files?.[0]; if (!f) return;
                const fd = new FormData(); fd.append('file', f);
                try {
                  toast.info('Video yükleniyor...');
                  const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/video`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }, body: fd });
                  if (!r.ok) throw new Error('upload failed');
                  const d = await r.json(); setFormData((p) => ({ ...p, video_file: d.url })); toast.success('Video yüklendi');
                } catch { toast.error('Video yüklenemedi'); }
              }} style={{ color: '#94A3B8' }} />
              {formData.video_file && <span className="text-xs" style={{ color: '#22C55E' }}>Video yüklü ✓ <button type="button" onClick={() => setFormData({ ...formData, video_file: '' })} style={{ color: '#EF4444' }}>(kaldır)</button></span>}
            </div>
            <input type="text" name="video_url" value={formData.video_url} onChange={handleChange} placeholder="veya YouTube linki yapıştırın"
              data-testid="buildlog-video-url" className="w-full px-4 py-3 rounded-lg" style={{ background: 'rgba(15,23,42,0.6)', color: '#F8FAFC', border: '1px solid rgba(0,240,255,0.3)' }} />
          </div>
          <div className="lg:col-span-3">
            <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>{t('admin.buildLogs.form.date')}</label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg"
              style={{
                background: 'rgba(15, 23, 42, 0.8)',
                border: '1px solid rgba(0, 240, 255, 0.3)',
                color: '#F8FAFC'
              }}
            />
          </div>
        </div>

        {/* Language Tabs */}
        <div className="flex items-center gap-2 p-1 rounded-lg" style={{ background: 'rgba(15, 23, 42, 0.6)' }}>
          <button
            type="button"
            onClick={() => setActiveTab('en')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
            style={{
              background: activeTab === 'en' ? 'rgba(0, 240, 255, 0.2)' : 'transparent',
              color: activeTab === 'en' ? '#00F0FF' : '#94A3B8',
              border: activeTab === 'en' ? '1px solid #00F0FF' : '1px solid transparent'
            }}
          >
            <Globe size={16} />
            {t('admin.common.english')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('tr')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
            style={{
              background: activeTab === 'tr' ? 'rgba(239, 68, 68, 0.2)' : 'transparent',
              color: activeTab === 'tr' ? '#EF4444' : '#94A3B8',
              border: activeTab === 'tr' ? '1px solid #EF4444' : '1px solid transparent'
            }}
          >
            <Globe size={16} />
            {t('admin.common.turkish')}
          </button>
        </div>

        {/* English Fields */}
        {activeTab === 'en' && (
          <div className="space-y-6" data-testid="en-fields">
            <div>
              <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>
                {t('admin.buildLogs.form.titleEn')} *
              </label>
              <input
                type="text"
                name="title"
                required
                value={formData.title}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg"
                style={{
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(0, 240, 255, 0.3)',
                  color: '#F8FAFC'
                }}
                placeholder="Week 12: First Autonomous Flight Test"
              />
            </div>
            <div>
              <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>
                {t('admin.buildLogs.form.excerptEn')} *
              </label>
              <textarea
                name="excerpt"
                required
                rows={2}
                value={formData.excerpt}
                onChange={handleChange}
                className="w-full px-4 py-3 resize-none rounded-lg"
                style={{
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(0, 240, 255, 0.3)',
                  color: '#F8FAFC'
                }}
                placeholder="Brief summary in English..."
              />
            </div>
            <div>
              <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>
                {t('admin.buildLogs.form.contentEn')} *
              </label>
              <textarea
                name="content"
                required
                rows={10}
                value={formData.content}
                onChange={handleChange}
                className="w-full px-4 py-3 resize-none rounded-lg"
                style={{
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(0, 240, 255, 0.3)',
                  color: '#F8FAFC'
                }}
                placeholder="Detailed content in English..."
              />
            </div>
          </div>
        )}

        {/* Turkish Fields */}
        {activeTab === 'tr' && (
          <div className="space-y-6" data-testid="tr-fields">
            <div className="p-4 rounded-lg" style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
              <p className="text-sm" style={{ color: '#F8FAFC' }}>
                💡 {t('admin.buildLogs.form.trNote')}
              </p>
            </div>
            <div>
              <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>
                {t('admin.buildLogs.form.titleTr')}
              </label>
              <input
                type="text"
                name="title_tr"
                value={formData.title_tr}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg"
                style={{
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#F8FAFC'
                }}
                placeholder="Hafta 12: İlk Otonom Uçuş Testi"
              />
            </div>
            <div>
              <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>
                {t('admin.buildLogs.form.excerptTr')}
              </label>
              <textarea
                name="excerpt_tr"
                rows={2}
                value={formData.excerpt_tr}
                onChange={handleChange}
                className="w-full px-4 py-3 resize-none rounded-lg"
                style={{
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#F8FAFC'
                }}
                placeholder="Türkçe kısa özet..."
              />
            </div>
            <div>
              <label className="block text-sm mb-2" style={{ color: '#F8FAFC' }}>
                {t('admin.buildLogs.form.contentTr')}
              </label>
              <textarea
                name="content_tr"
                rows={10}
                value={formData.content_tr}
                onChange={handleChange}
                className="w-full px-4 py-3 resize-none rounded-lg"
                style={{
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#F8FAFC'
                }}
                placeholder="Türkçe detaylı içerik..."
              />
            </div>
          </div>
        )}

        <div className="flex items-center gap-4 pt-4" style={{ borderTop: '1px solid rgba(0, 240, 255, 0.1)' }}>
          <button
            type="button"
            onClick={() => navigate('/admin/build-logs')}
            className="px-6 py-3 rounded-lg"
            style={{ color: '#94A3B8', background: 'rgba(148, 163, 184, 0.1)' }}
          >
            <X size={18} className="inline mr-2" />
            {t('admin.common.cancel')}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-3 rounded-lg flex items-center gap-2"
            style={{
              background: 'linear-gradient(90deg, #00F0FF 0%, #0F172A 100%)',
              color: '#F8FAFC',
              border: '2px solid #00F0FF',
            }}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save size={18} />}
            {isEdit ? t('admin.common.update') : t('admin.common.save')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminBuildLogs;
